<template>
  <form
    class="space-y-4"
    @submit.prevent="$emit('submit')"
  >
    <!-- Name -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {{ t('app.sysadmin.create_restaurant.name') }} *
      </label>
      <input
        :value="form.name"
        type="text"
        required
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        :placeholder="t('app.sysadmin.create_restaurant.name_placeholder')"
        @input="$emit('update:form', { ...form, name: ($event.target as HTMLInputElement).value })"
      >
    </div>

    <!-- Subdomain -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {{ t('app.sysadmin.create_restaurant.subdomain') }} *
      </label>
      <div class="flex items-center gap-2">
        <input
          :value="form.subdomain"
          type="text"
          required
          pattern="[a-z0-9\-]+"
          class="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          :placeholder="t('app.sysadmin.create_restaurant.subdomain_placeholder')"
          @input="handleSubdomainInput"
        >
        <span class="text-sm text-gray-500 dark:text-gray-400">.tudominio.com</span>
      </div>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {{ t('app.sysadmin.create_restaurant.subdomain_hint') }}
      </p>
    </div>

    <!-- Business Type -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Tipo de Negocio *
      </label>
      <select
        :value="form.business_type"
        required
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        @change="$emit('update:form', { ...form, business_type: ($event.target as HTMLSelectElement).value })"
      >
        <option value="restaurant">
          🍽️ Restaurante
        </option>
        <option value="cafe">
          ☕ Cafetería
        </option>
        <option value="food_truck">
          🚚 Food Truck
        </option>
        <option value="churreria">
          🥨 Churrería
        </option>
        <option value="bakery">
          🥖 Panadería
        </option>
        <option value="bar">
          🍺 Bar
        </option>
        <option value="fast_food">
          🍔 Comida Rápida
        </option>
        <option value="other">
          🏪 Otro
        </option>
      </select>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Selecciona el tipo de negocio para personalizar la experiencia
      </p>
    </div>

    <!-- Email -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {{ t('app.sysadmin.create_restaurant.email') }} *
      </label>
      <input
        :value="form.email"
        type="email"
        required
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        :placeholder="t('app.sysadmin.create_restaurant.email_placeholder')"
        @input="$emit('update:form', { ...form, email: ($event.target as HTMLInputElement).value })"
      >
    </div>

    <!-- Admin Email -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Email del Administrador *
      </label>
      <input
        :value="form.admin_email"
        type="email"
        required
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        placeholder="admin@restaurante.com"
        @input="$emit('update:form', { ...form, admin_email: ($event.target as HTMLInputElement).value })"
      >
    </div>

    <!-- Phone -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Teléfono
      </label>
      <input
        :value="form.phone"
        type="tel"
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        placeholder="+52 123 456 7890"
        @input="$emit('update:form', { ...form, phone: ($event.target as HTMLInputElement).value })"
      >
    </div>

    <!-- Address -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Dirección
      </label>
      <textarea
        :value="form.address"
        rows="3"
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        placeholder="Calle Principal #123, Colonia Centro, Ciudad, Estado"
        @input="$emit('update:form', { ...form, address: ($event.target as HTMLTextAreaElement).value })"
      />
    </div>

    <!-- Description -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Descripción
      </label>
      <textarea
        :value="form.description"
        rows="3"
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        placeholder="Describe brevemente tu restaurante..."
        @input="$emit('update:form', { ...form, description: ($event.target as HTMLTextAreaElement).value })"
      />
    </div>

    <!-- Plan Type Selection -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
        Tipo de Plan *
      </label>
      <div class="grid grid-cols-2 gap-3">
        <button
          type="button"
          class="flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg transition-all"
          :class="[
            selectedPlanType === 'trial' 
              ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' 
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          ]"
          @click="$emit('update:selectedPlanType', 'trial')"
        >
          <div
            :class="[
              'w-4 h-4 rounded-full border-2 flex items-center justify-center',
              selectedPlanType === 'trial' ? 'border-blue-600' : 'border-gray-400'
            ]"
          >
            <div
              v-if="selectedPlanType === 'trial'"
              class="w-2 h-2 rounded-full bg-blue-600"
            />
          </div>
          <span class="text-sm font-medium text-gray-900 dark:text-white">Período de Prueba</span>
        </button>
        <button
          type="button"
          class="flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg transition-all"
          :class="[
            selectedPlanType === 'paid' 
              ? 'border-green-600 bg-green-50 dark:bg-green-900/20' 
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          ]"
          @click="$emit('update:selectedPlanType', 'paid')"
        >
          <div
            :class="[
              'w-4 h-4 rounded-full border-2 flex items-center justify-center',
              selectedPlanType === 'paid' ? 'border-green-600' : 'border-gray-400'
            ]"
          >
            <div
              v-if="selectedPlanType === 'paid'"
              class="w-2 h-2 rounded-full bg-green-600"
            />
          </div>
          <span class="text-sm font-medium text-gray-900 dark:text-white">Plan de Pago</span>
        </button>
      </div>
    </div>

    <!-- Trial Days Selector (only if trial selected) -->
    <div v-if="selectedPlanType === 'trial'">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {{ t('app.sysadmin.create_restaurant.trial_days') }} *
      </label>
      <div
        class="grid grid-cols-1 gap-3"
        :class="selectedTrialOption === 'custom' ? 'sm:grid-cols-2' : ''"
      >
        <select
          :value="selectedTrialOption"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          @change="$emit('update:selectedTrialOption', ($event.target as HTMLSelectElement).value === 'custom' ? 'custom' : Number(($event.target as HTMLSelectElement).value))"
        >
          <option :value="14">
            14 {{ t('app.common.days') }} ({{ t('app.sysadmin.create_restaurant.trial_default') }})
          </option>
          <option :value="30">
            30 {{ t('app.common.days') }} (1 {{ t('app.common.month') }})
          </option>
          <option :value="60">
            60 {{ t('app.common.days') }} (2 {{ t('app.common.months') }})
          </option>
          <option value="custom">
            Fecha personalizada...
          </option>
        </select>
                
        <div v-if="selectedTrialOption === 'custom'">
          <input 
            :value="customTrialDate"
            type="date"
            :min="minDate"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
            @input="$emit('update:customTrialDate', ($event.target as HTMLInputElement).value)"
          >
        </div>
      </div>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {{ selectedTrialOption === 'custom' 
          ? `El período de prueba durará ${form.trial_days} días`
          : t('app.sysadmin.create_restaurant.trial_days_hint') 
        }}
      </p>
    </div>

    <!-- Plan Selector (only if paid selected) -->
    <div v-if="selectedPlanType === 'paid'">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Plan de Suscripción *
      </label>
      <select
        :value="form.plan_id"
        required
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        @change="$emit('update:form', { ...form, plan_id: Number(($event.target as HTMLSelectElement).value) })"
      >
        <option
          :value="null"
          disabled
        >
          Selecciona un plan...
        </option>
        <option 
          v-for="plan in availablePlans" 
          :key="plan.id" 
          :value="plan.id"
        >
          {{ plan.display_name }} - ${{ plan.monthly_price.toFixed(2) }}/mes
        </option>
      </select>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        El restaurante comenzará con una suscripción activa del plan seleccionado
      </p>
    </div>

    <!-- Info Box -->
    <div
      :class="[
        'rounded-lg p-4',
        selectedPlanType === 'trial' 
          ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
          : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
      ]"
    >
      <div class="flex items-start gap-3">
        <svg
          class="h-5 w-5 mt-0.5"
          :class="selectedPlanType === 'trial' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div class="flex-1">
          <p
            class="text-sm font-medium"
            :class="selectedPlanType === 'trial' ? 'text-blue-900 dark:text-blue-200' : 'text-green-900 dark:text-green-200'"
          >
            {{ selectedPlanType === 'trial' ? t('app.sysadmin.create_restaurant.trial_info_title') : 'Suscripción de Pago' }}
          </p>
          <p
            class="text-sm mt-1"
            :class="selectedPlanType === 'trial' ? 'text-blue-700 dark:text-blue-300' : 'text-green-700 dark:text-green-300'"
          >
            {{ selectedPlanType === 'trial' 
              ? t('app.sysadmin.create_restaurant.trial_info_desc')
              : 'El restaurante comenzará con acceso completo a las funcionalidades del plan seleccionado.'
            }}
          </p>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { SubscriptionPlan } from '@/services/subscriptionService';
import type { RestaurantForm, PlanType, TrialOption } from './createRestaurantTypes';

interface Props {
  form: RestaurantForm;
  selectedPlanType: PlanType;
  selectedTrialOption: TrialOption;
  customTrialDate: string;
  availablePlans: SubscriptionPlan[];
  minDate: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'submit'): void;
  (e: 'update:form', value: RestaurantForm): void;
  (e: 'update:selectedPlanType', value: PlanType): void;
  (e: 'update:selectedTrialOption', value: TrialOption): void;
  (e: 'update:customTrialDate', value: string): void;
}>();

const { t } = useI18n();

const handleSubdomainInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  // Only allow lowercase letters, numbers, and hyphens
  input.value = input.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
  emit('update:form', { ...props.form, subdomain: input.value });
};
</script>
